import { inject, injectable } from "tsyringe";
import { COOKIES, UUID, WINDOW } from "@identity/symbols";
import { Browser, Platform, WikiConfig } from "@identity/utils";
import { BeaconGenerator, PageViewNumberGenerator } from "@identity/services";
import { createAdTargetingGroup } from "./create-ad-targeting-group";

@injectable()
export class FandomContextCreator {
  constructor(
    @inject(COOKIES) private readonly cookiesManager: CookiesManager,
    @inject(WikiConfig) private readonly wikiConfig: WikiConfig,
    @inject(WINDOW)
    private readonly window: Pick<Window, "RLCONF" | "pvUID" | "sessionId">,
    @inject(UUID) private uuidProvider: UuidProvider,
    @inject(PageViewNumberGenerator)
    private pageViewNumberGenerator: PageViewNumberGenerator,
    @inject(BeaconGenerator) private beaconGenerator: BeaconGenerator,
    @inject(Browser) private browser: Browser,
    @inject(Platform) private readonly platform: Platform,
  ) {}

  public createTrackingContext(): TrackingContext {
    const beaconId =
      this.cookiesManager.get("wikia_beacon_id") ||
      this.cookiesManager.get("beaconId") ||
      this.beaconGenerator.generate();
    const sessionId =
      this.wikiConfig.get("sessionId", this.window.sessionId) ||
      this.cookiesManager.get("sessionId") ||
      this.uuidProvider();
    const userId =
      this.wikiConfig.get("userId") || this.window.RLCONF?.wgUserId || 0;

    return {
      ...this.createPageViewTrackingContext(),
      beaconId,
      sessionId,
      userId,
    };
  }

  public createTargetingContext(): TargetingContext {
    const browser = this.browser.getBrowserName();
    const brand = this.platform.getBrand();
    const adGroup = createAdTargetingGroup();

    return {
      browser,
      brand,
      adGroup,
    };
  }

  public createPartnersContext(): Pick<
    PartnersContext,
    "anyclip" | "blockthrough"
  > {
    const blockthrough = {};
    const anyclip = {};

    return {
      blockthrough,
      anyclip,
    };
  }

  public createPageViewTrackingContext(): Pick<
    TrackingContext,
    "pvUID" | "pvNumber" | "pvNumberGlobal"
  > {
    const pvUID =
      this.wikiConfig.get("pvUID") || // UCP
      this.window.pvUID || // F2
      this.uuidProvider();
    const pvNumber =
      this.pageViewNumberGenerator.getNextNumberFromCookie("pvNumber");
    const pvNumberGlobal =
      this.pageViewNumberGenerator.getNextNumberFromCookie("pvNumberGlobal");

    return {
      pvUID,
      pvNumber,
      pvNumberGlobal,
    };
  }
}
